import Head from 'next/head'
import Router from 'next/router'
import Script from 'next/script'
import type { AppProps } from 'next/app'
import { useEffect } from 'react'
import { appWithTranslation, useTranslation } from 'next-i18next'
import { ThirdwebProvider, embeddedWallet, metamaskWallet, coinbaseWallet } from '@thirdweb-dev/react'
import { CronosTestnet, Polygon, PolygonAmoyTestnet } from '@thirdweb-dev/chains'
import { config } from '@fortawesome/fontawesome-svg-core'
import '@fortawesome/fontawesome-svg-core/styles.css'
import { environment } from '~/constants/environment'
import { thirdwebClientId } from '~/constants/thirdweb'
import { AppMenu } from '~/components/AppMenu/AppMenu'
import { AppFooter } from '~/components/AppFooter/AppFooter'
import { Settings } from 'luxon'
import { localeWithTerritory } from '~/modules/Shared/Domain/Helpers/Locale'
import { SessionProvider } from 'next-auth/react'
import { AnalyticsIdentify } from '~/modules/Analytics/Infrastructure/Components/AnalyticsIdentify'
import UserProvider from '~/modules/Auth/Infrastructure/Components/UserProvider'
import { UserTosModal } from '~/modules/Auth/Infrastructure/Components/UserTosModal'
import { buildAnalyticsBootstrapScript, ensureBrowserAnalyticsClient } from '~/modules/Analytics/Infrastructure/browserAnalyticsRuntime'
import { buildGoogleTagManagerScript, getGoogleTagManagerId } from '~/modules/Analytics/Infrastructure/googleTagManager'
import { safeAnalyticsPage } from '~/modules/Analytics/Infrastructure/safeAnalytics'
import { resolveAppShellAppearance } from '~/modules/UiVariant/appShellAppearance'
import '~/styles/globals.scss'
import styles from '~/styles/pages/_app.module.scss'

config.autoAddCss = false

function TrophyApp ({
  Component,
  pageProps: {
    session,
    ...pageProps
  },
  router,
}: AppProps) {
  const { i18n } = useTranslation()
  const googleTagManagerId = getGoogleTagManagerId()
  const sgdEndpoint = process.env.NEXT_PUBLIC_SGD_ENDPOINT?.trim() || null
  const sgdScriptProps = sgdEndpoint
    ? { 'data-sgd-endpoint': sgdEndpoint }
    : {}
  const shellAppearance = resolveAppShellAppearance({ pathname: router.pathname })

  useEffect(() => {
    ensureBrowserAnalyticsClient()
    safeAnalyticsPage()

    const onRouteChangeComplete = () => safeAnalyticsPage()

    Router.events.on('routeChangeComplete', onRouteChangeComplete)

    return () => {
      Router.events.off('routeChangeComplete', onRouteChangeComplete)
    }
  }, [])

  Settings.defaultLocale = localeWithTerritory(i18n.language || 'en')
    .replace('_', '-')

  // TODO: find the way to get user timezone and set it on Luxon Settings
  Settings.defaultZone = 'Europe/Madrid'

  return (
    <SessionProvider session={ session }>
      <UserProvider>
        <ThirdwebProvider
          clientId={ thirdwebClientId }
          activeChain={ environment === 'production' ? Polygon : PolygonAmoyTestnet }
          supportedChains={ environment === 'production' ? [Polygon] : [PolygonAmoyTestnet, CronosTestnet] }
          supportedWallets={ [
            embeddedWallet(),
            metamaskWallet(),
            coinbaseWallet(),
          ] }
        >
          <>
            <Script
              id="analytics-shim"
              strategy="beforeInteractive"
              dangerouslySetInnerHTML={ { __html: buildAnalyticsBootstrapScript() } }
            />

            {
              googleTagManagerId &&
              <Script
                id="gtm-script"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={ { __html: buildGoogleTagManagerScript(googleTagManagerId) } }
              />
            }

            <Script
              id="sgd-script"
              src="/sgd.js"
              strategy="afterInteractive"
              data-sgd-cookie-domain=".belegends.com"
              { ...sgdScriptProps }
            />

            <AnalyticsIdentify />

            <Head>
              <link rel="icon" href="/favicon.ico" />
            </Head>

            <UserTosModal />

            <AppMenu appearance={ shellAppearance } />

            <main className={ styles.app__container }>
              <Component { ...pageProps } />
            </main>

            <AppFooter appearance={ shellAppearance } />
          </>
        </ThirdwebProvider>
      </UserProvider>
    </SessionProvider>
  )
}

export default appWithTranslation(TrophyApp)
